import { override, observable, makeObservable, computed, action } from "mobx";
import { MrbBaseEditViewStore, httpStatusCodes } from "mrb/core";
import { localizationService } from "common/localization";
import { merge } from "lodash";
import { UserProfileEditForm } from "application/user-settings/forms";
import { MrbModalParams } from "mrb/common/models";
import { MrbSelectStore } from "mrb/components/select";
import { DropdownItemsResult } from "mrb/components/select/models";

class UserProfileEditViewStore extends MrbBaseEditViewStore {
    administratorRole = null;

    @observable.ref certifiedModes = [];
    @observable selectedCertifiedModeId = null;
    @observable isWhitelistMode = false;

    @computed get shouldDisplayContentModeOptions() {
        return this.rootStore.premiumSubscriptionStore.hasPortalAccess;
    }

    @computed get isTrendingHidden() {
        return this.rootStore.userStore.isTrendingHidden;
    }

    @computed get selectedModeValue() {
        if (this.selectedCertifiedModeId) return this.selectedCertifiedModeId;
        if (this.isWhitelistMode) return "whitelist";
        return "naki-standard";
    }

    constructor(rootStore, { routeStore }) {
        super(rootStore, {
            id: rootStore.userStore.user.id,
            name: "user",
            FormClass: UserProfileEditForm,
            autoFocusField: "firstName",
            successCreateNotification: null,
            successUpdateNotification: localizationService.t("APPLICATION.USER_SETTINGS.EDIT.SUCCESS_MESSAGE"),
            redirectOnUpdateSuccess: false,
        });
        makeObservable(this);
        this.routeStore = routeStore;
        this.certifiedModeInfoModal = new MrbModalParams(null, null);
        this.selectedCertifiedModeId = this.rootStore.premiumSubscriptionStore.certifiedModeId;
        this.isWhitelistMode = this.rootStore.premiumSubscriptionStore.isWhitelistMode;
        this.openCertifiedModeInfoModal = this.openCertifiedModeInfoModal.bind(this);
        this.modeSelectStore = new MrbSelectStore({
            isSearchable: false,
            virtualized: false,
            initFetch: false,
            isClearable: false,
            textKey: "name",
            valueKey: "id",
            actions: {
                onChange: (option) => {
                    if (option) {
                        this.setCertifiedModeSelection(option.value);
                    }
                },
            },
        });
        this.modeSelectStore.originalItems = new DropdownItemsResult([], 1, 0, 0);
        this.loadCertifiedModes();
    }

    get() {
        return this.routeStore.getResource();
    }

    update(data) {
        const resource = merge({}, this.item);
        resource.firstName = data.firstName;
        resource.lastName = data.lastName;
        resource.email = data.email;
        resource.roles = null;

        return this.routeStore.update(resource);
    }

    openCertifiedModeInfoModal() {
        this.certifiedModeInfoModal.open();
    }

    @action.bound
    async setCertifiedModeSelection(value) {
        const prevCertifiedModeId = this.selectedCertifiedModeId;
        const prevWhitelistMode = this.isWhitelistMode;

        let certifiedModeId = null;
        let whitelistMode = false;
        if (value === "whitelist") {
            whitelistMode = true;
        } else if (value !== "naki-standard") {
            certifiedModeId = value;
        }

        this.selectedCertifiedModeId = certifiedModeId;
        this.isWhitelistMode = whitelistMode;

        try {
            await this.routeStore.updateCertifiedMode(certifiedModeId, whitelistMode);
            const selectedMode = this.certifiedModes.find((m) => m.id === certifiedModeId);
            this.rootStore.premiumSubscriptionStore.setCertifiedMode(
                certifiedModeId,
                selectedMode ? selectedMode.name : null,
                whitelistMode
            );
            this.rootStore.notificationStore.success(
                localizationService.t("APPLICATION.USER_SETTINGS.EDIT.FIELDS.CERTIFIED_MODE.SUCCESS_MESSAGE")
            );
        } catch (error) {
            this.selectedCertifiedModeId = prevCertifiedModeId;
            this.isWhitelistMode = prevWhitelistMode;
            this._syncModeSelectStore(prevCertifiedModeId, prevWhitelistMode);
            this.rootStore.notificationStore.error(
                localizationService.t("APPLICATION.USER_SETTINGS.EDIT.FIELDS.CERTIFIED_MODE.ERROR_MESSAGE"),
                error
            );
        }
    }

    @action.bound
    async loadCertifiedModes() {
        try {
            const response = await this.routeStore.getCertifiedModes();
            this.certifiedModes = response.item || response || [];
        } catch (error) {
            this.certifiedModes = [];
        }
        const allOptions = [
            { id: "naki-standard", name: "The Naki Standard" },
            { id: "whitelist", name: "Whitelist Mode" },
            ...this.certifiedModes.map((m) => ({
                id: m.id,
                name: `${m.name}`,
            })),
        ];
        this.modeSelectStore.originalItems = new DropdownItemsResult(
            allOptions,
            1,
            allOptions.length,
            allOptions.length
        );
        this._syncModeSelectStore(this.selectedCertifiedModeId, this.isWhitelistMode);
    }

    _syncModeSelectStore(certifiedModeId, isWhitelistMode) {
        let option;
        if (certifiedModeId) {
            const mode = this.certifiedModes.find((m) => m.id === certifiedModeId);
            option = mode ? { id: mode.id, name: `${mode.name}` } : { id: "naki-standard", name: "The Naki Standard" };
        } else if (isWhitelistMode) {
            option = { id: "whitelist", name: "Whitelist Mode" };
        } else {
            option = { id: "naki-standard", name: "The Naki Standard" };
        }
        this.modeSelectStore.setSelectedItem(option);
    }

    @action.bound
    async updateHideTrending(value) {
        try {
            await this.rootStore.userStore.updateHideTrending(value);
        } catch (error) {
            this.rootStore.notificationStore.error(
                localizationService.t("APPLICATION.USER_SETTINGS.EDIT.FIELDS.HIDE_TRENDING.ERROR_MESSAGE"),
                error
            );
        }
    }

    @override
    updateForm(values) {
        this.form.set({
            firstName: values.userProfile && values.userProfile.firstName,
            lastName: values.userProfile && values.userProfile.lastName,
            email: values.email,
        });
    }

    onUpdateError(error) {
        if (error.statusCode && error.statusCode === httpStatusCodes.BadRequest) {
            this.form.$("email").invalidate("User with specified E-mail address already exists.");
        } else {
            this.rootStore.notificationStore.error(
                localizationService.t("APPLICATION.USER_SETTINGS.EDIT.ERROR.GENERIC_MESSAGE"),
                error
            );
        }
    }
}

export default UserProfileEditViewStore;
